import { useLenis } from 'lenis/react'
import { useEffect, useRef, type MouseEvent } from 'react'
import { Button, Magnetic } from '@/components/ui'
import { nav, site } from '@/content/site'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { EASE, gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { clamp, damp, smoothstep } from '@/lib/math'
import { pointer } from '@/lib/pointer'
import { useAppStore } from '@/lib/store'
import { Menu } from './Menu'
import styles from './Nav.module.css'
import { createNavTracker, type NavTracker } from './navTracker'
import { scrollToSection } from './scrollToSection'

const LINK_IDS: ReadonlySet<string> = new Set(nav.links.map((link) => link.href.slice(1)))

/** Ignore modified clicks (new tab, etc.) and let the browser handle them. */
function isPlainClick(event: MouseEvent): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}

/**
 * The top bar: wordmark left, section links and the magnetic CTA right; below 1024px a
 * menu toggle opens the full-screen glass menu.
 *
 * - Hides on scroll down and returns on scroll up (damped CSS transform), always shown near
 *   the top, under reduced motion, while the menu is open or keyboard focus is inside.
 * - Clear over the hero, then a frosted glass pill. On desktop the glass catches the
 *   pointer's light: a soft sheen and an iridescent rim glide with it.
 * - A small iris droplet glides to the active section's link; links glide with Lenis.
 * - Colours follow the section under the bar via `data-theme` (semantic tokens).
 */
export function Nav() {
  const headerRef = useRef<HTMLElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLElement>(null)
  const dotRef = useRef<HTMLSpanElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const trackerRef = useRef<NavTracker | null>(null)
  const moveIndicatorRef = useRef<(id: string | null) => void>(() => {})
  const pinnedRef = useRef(false)

  const lenis = useLenis()
  const introStarted = useAppStore((state) => state.phase !== 'boot')
  const menuOpen = useAppStore((state) => state.menuOpen)
  const setMenuOpen = useAppStore((state) => state.setMenuOpen)
  const reducedMotion = useReducedMotion()
  const desktop = useMediaQuery(MEDIA.desktop)
  const finePointer = useMediaQuery(MEDIA.hover)
  pinnedRef.current = reducedMotion || menuOpen

  // Scroll-derived state, written straight to the DOM (no re-render while scrolling).
  useEffect(() => {
    const header = headerRef.current
    if (!header) return
    let focusInside = false
    const tracker = createNavTracker(header, {
      pinned: () => pinnedRef.current || focusInside,
      linkIds: LINK_IDS,
      onActive: (id) => {
        header.querySelectorAll<HTMLElement>('[data-nav-target]').forEach((link) => {
          if (link.dataset.navTarget === id) link.setAttribute('aria-current', 'true')
          else link.removeAttribute('aria-current')
        })
        moveIndicatorRef.current(id)
      },
    })
    trackerRef.current = tracker

    let frame = 0
    const remeasure = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        tracker.measure()
        tracker.update(window.scrollY)
      })
    }
    remeasure()
    ScrollTrigger.addEventListener('refresh', remeasure)
    const resizeObserver = new ResizeObserver(remeasure)
    resizeObserver.observe(document.body)

    const onFocusIn = () => {
      focusInside = true
      tracker.update(window.scrollY)
    }
    const onFocusOut = (event: FocusEvent) => {
      if (!header.contains(event.relatedTarget as Node | null)) focusInside = false
    }
    header.addEventListener('focusin', onFocusIn)
    header.addEventListener('focusout', onFocusOut)

    return () => {
      window.cancelAnimationFrame(frame)
      ScrollTrigger.removeEventListener('refresh', remeasure)
      resizeObserver.disconnect()
      header.removeEventListener('focusin', onFocusIn)
      header.removeEventListener('focusout', onFocusOut)
      trackerRef.current = null
    }
  }, [])

  useLenis((instance) => trackerRef.current?.update(instance.scroll), [])

  useEffect(() => {
    trackerRef.current?.update(window.scrollY)
  }, [menuOpen, reducedMotion])

  // The menu is a small-screen pattern: leaving it open across the breakpoint makes no sense.
  useEffect(() => {
    if (desktop && useAppStore.getState().menuOpen) setMenuOpen(false)
  }, [desktop, setMenuOpen])

  // Active-section droplet: measured on resize, never during scroll.
  useEffect(() => {
    const primary = primaryRef.current
    const dot = dotRef.current
    if (!primary || !dot || !desktop) {
      moveIndicatorRef.current = () => {}
      return
    }
    let centers = new Map<string, number>()
    let current: string | null = null
    const measure = () => {
      centers = new Map(
        Array.from(primary.querySelectorAll<HTMLElement>('[data-nav-target]'), (link) => [
          link.dataset.navTarget ?? '',
          link.offsetLeft + link.offsetWidth / 2,
        ]),
      )
      const x = current ? centers.get(current) : undefined
      if (x !== undefined) gsap.set(dot, { x })
    }
    measure()
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(primary)
    let cancelled = false
    void document.fonts.ready.then(() => {
      if (!cancelled) measure()
    })

    moveIndicatorRef.current = (id) => {
      const reduced = useAppStore.getState().reducedMotion
      const x = id ? centers.get(id) : undefined
      if (x === undefined) {
        current = null
        gsap.to(dot, { autoAlpha: 0, scale: 0.4, duration: reduced ? 0 : 0.7, ease: EASE.out })
        return
      }
      if (current === null || reduced) {
        gsap.killTweensOf(dot)
        gsap.set(dot, { x, scaleX: 1 })
        gsap.to(dot, { autoAlpha: 1, scale: 1, duration: reduced ? 0 : 0.8, ease: EASE.out })
      } else {
        // A droplet: it stretches as it travels and settles round.
        const distance = Math.abs(x - (centers.get(current) ?? x))
        gsap.to(dot, { x, duration: 1, ease: EASE.inOut, overwrite: 'auto' })
        gsap
          .timeline()
          .to(dot, { scaleX: 1 + Math.min(distance / 30, 3.6), duration: 0.42, ease: 'sine.out' })
          .to(dot, { scaleX: 1, duration: 0.75, ease: EASE.out })
      }
      current = id
    }
    trackerRef.current?.update(window.scrollY)

    return () => {
      cancelled = true
      resizeObserver.disconnect()
      gsap.killTweensOf(dot)
      moveIndicatorRef.current = () => {}
    }
  }, [desktop])

  // Light-catching glass (desktop, fine pointer): one ticker, idle when the pointer is far.
  useEffect(() => {
    const bar = barRef.current
    if (!bar || !desktop || !finePointer || reducedMotion) return
    let left = 0
    let width = 1
    const measure = () => {
      const rect = bar.getBoundingClientRect()
      left = rect.left
      width = Math.max(1, rect.width)
    }
    measure()
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(bar)

    let lightX = width / 2
    let intensity = 0
    let written = ''
    const tick = (_time: number, deltaMs: number) => {
      const dt = Math.min(Math.max(deltaMs, 1), 64) / 1000
      const near = pointer.lastMove > 0 ? 1 - smoothstep(30, 210, pointer.y) : 0
      lightX = damp(lightX, clamp(pointer.x - left, 0, width), 7, dt)
      intensity = damp(intensity, near, 3.5, dt)
      if (near === 0 && intensity < 0.002) {
        if (written !== 'off') {
          written = 'off'
          bar.style.setProperty('--light', '0')
        }
        return
      }
      const next = `${lightX.toFixed(1)}|${intensity.toFixed(3)}`
      if (next === written) return
      written = next
      bar.style.setProperty('--light-x', `${lightX.toFixed(1)}px`)
      bar.style.setProperty('--light', intensity.toFixed(3))
    }
    gsap.ticker.add(tick)
    return () => {
      gsap.ticker.remove(tick)
      resizeObserver.disconnect()
      bar.style.removeProperty('--light')
    }
  }, [desktop, finePointer, reducedMotion])

  // Intro: the chrome settles in once the preloader starts opening.
  useGSAP(
    () => {
      const items = gsap.utils.toArray<HTMLElement>('[data-intro]', headerRef.current)
      if (!introStarted) {
        gsap.set(items, { autoAlpha: 0 })
        return
      }
      if (useAppStore.getState().reducedMotion) {
        gsap.fromTo(items, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, delay: 0.15 })
        return
      }
      gsap.fromTo(
        items,
        { autoAlpha: 0, y: -14 },
        { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.035, ease: EASE.out, delay: 0.15 },
      )
    },
    { scope: headerRef, dependencies: [introStarted], revertOnUpdate: true },
  )

  const goTo = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!isPlainClick(event)) return
    // Lenis listens for anchor clicks on window; this glide (and its easing) is ours.
    event.preventDefault()
    event.stopPropagation()
    const fromMenu = useAppStore.getState().menuOpen
    // Under the open menu the page can jump: the menu retracts onto the section.
    scrollToSection(lenis, href, { immediate: fromMenu || reducedMotion })
    if (fromMenu) setMenuOpen(false)
  }

  const closeMenuFromKeyboard = () => {
    setMenuOpen(false)
    toggleRef.current?.focus()
  }

  return (
    <header
      ref={headerRef}
      className={styles.nav}
      data-theme="light"
      data-hidden="false"
      data-glass="false"
      data-menu={menuOpen}
      inert={!introStarted}
    >
      <div ref={barRef} className={styles.bar}>
        <span className={styles.glass} aria-hidden="true" />
        <span className={styles.light} aria-hidden="true" />

        <a
          className={styles.wordmark}
          href="#hero"
          onClick={(event) => goTo(event, '#hero')}
          data-intro
        >
          {site.name}
        </a>

        <div className={styles.end}>
          <nav ref={primaryRef} className={styles.primary} aria-label={nav.ui.label}>
            <ul className={styles.links} role="list">
              {nav.links.map((link) => (
                <li key={link.href} data-intro>
                  <Magnetic strength={0.3}>
                    <a
                      className={styles.link}
                      href={link.href}
                      data-nav-target={link.href.slice(1)}
                      onClick={(event) => goTo(event, link.href)}
                    >
                      <span className={styles.roll}>
                        <span className={styles.rollText}>{link.label}</span>
                        <span className={styles.rollText} aria-hidden="true">
                          {link.label}
                        </span>
                      </span>
                    </a>
                  </Magnetic>
                </li>
              ))}
            </ul>
            <span ref={dotRef} className={styles.dot} aria-hidden="true" />
          </nav>

          <span className={styles.cta} data-intro>
            <Magnetic>
              <Button href={nav.cta.href} external icon="arrow-up-right">
                {nav.cta.label}
              </Button>
            </Magnetic>
          </span>

          <button
            ref={toggleRef}
            type="button"
            className={styles.toggle}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            onClick={() => setMenuOpen(!menuOpen)}
            data-intro
          >
            <span className={styles.toggleLabel}>{menuOpen ? nav.ui.close : nav.ui.menu}</span>
            <span className={styles.burger} aria-hidden="true">
              <span />
              <span />
            </span>
          </button>
        </div>
      </div>

      {desktop ? null : (
        <Menu
          open={menuOpen}
          headerRef={headerRef}
          toggleRef={toggleRef}
          onNavigate={goTo}
          onRequestClose={closeMenuFromKeyboard}
        />
      )}
    </header>
  )
}
